import React, { useEffect, useMemo } from 'react';
import SingleSelect from 'components/SingleSelect';
import { t } from '../../../../i18n';
import TransitInfoTable, { TransitInfoRow, TransitOption } from './TransitInfoTable';

/**
 * 是否转运下拉取值。后端不落库（是否转运由有无转运行派生），仅前端 UI 用。
 * 用字符串以适配 SingleSelect（Option value 会 toString）与 handleFilterChange 的 `value || ''`（数字 0 会被判 falsy）。
 */
export const IS_TRANSIT = { YES: '1', NO: '0' } as const;

interface TransitSectionProps {
    /** 是否存在转运目的地：'0'-否 '1'-是 */
    isTransit: string;
    onIsTransitChange: (value: string) => void;
    /** 转运行数据（由父级持有，保存时一并提交） */
    rows: TransitInfoRow[];
    onRowsChange: (rows: TransitInfoRow[]) => void;
    disabled?: boolean;
    /** 转运站候选（本路线途径站+终到站；已发车站已置灰不可选） */
    transitStationOptions?: TransitOption[];
    /** 转运目的地站候选（HUB list，已剔除本路线站点） */
    transitDestStationOptions?: TransitOption[];
}

/**
 * 转运配置（仅「干线 + 自有路线」任务使用，对应 tms_task_transit_info）。
 * - "是否存在转运目的地" 下拉（默认否）
 * - 选"是"时展示转运站 / 转运目的地站配置表格（多行增删，参考 BagInfoTable）
 *
 * 作为父级 grid(grid-cols-3) 的直接子项渲染：返回 Fragment、不额外包裹 div，
 * 以保证内部 col-start-1 / col-span-3 在父网格中生效。
 */
const TransitSection: React.FC<TransitSectionProps> = ({
    isTransit,
    onIsTransitChange,
    rows,
    onRowsChange,
    disabled,
    transitStationOptions = [],
    transitDestStationOptions = [],
}) => {
    const yesNoOptions = useMemo(() => [
        { value: IS_TRANSIT.YES, label: String(t('common.yes')) },
        { value: IS_TRANSIT.NO, label: String(t('common.no')) },
    ], []);

    // 存在锁定行(已装货的转运目的地)时，「是否转运」也不可改——否则切「否」会连带清掉锁定的转运
    const hasLockedRow = rows.some((r) => r.destLoaded);

    // 选"是"时必须至少有一行：为空则自动补一行
    useEffect(() => {
        if (isTransit === IS_TRANSIT.YES && rows.length === 0) {
            onRowsChange([{ key: `transit-${Date.now()}`, transitWarehouseId: '', transitDestWarehouseId: '' }]);
        }
    }, [isTransit, rows.length, onRowsChange]);

    // 切换是/否时清空表格（再次选"是"会由上面 useEffect 重新初始化一行，相当于重置）
    const handleHasTransitChange = (value: string) => {
        onRowsChange([]);
        onIsTransitChange(value);
    };

    return (
        <>
            <div className="col-start-1">
                <SingleSelect
                    label={String(t('tms.task.drawer.isTransit'))}
                    options={yesNoOptions}
                    value={isTransit}
                    onChange={(value) => handleHasTransitChange(value ?? IS_TRANSIT.NO)}
                    spanClassName="w-[7em]"
                    forceHorizontal
                    required
                    allowClear={false}
                    disabled={disabled || hasLockedRow}
                />
            </div>
            {isTransit === IS_TRANSIT.YES && (
                <div className="col-span-3">
                    <div className="flex">
                        {/* 空出与 label 等宽(w-[7em])的占位，使表格与输入区对齐 */}
                        <div className="w-[7em] flex-shrink-0 mr-2" />
                        <TransitInfoTable
                            dataSource={rows}
                            onChange={onRowsChange}
                            transitStationOptions={transitStationOptions}
                            transitDestStationOptions={transitDestStationOptions}
                            readOnly={disabled}
                        />
                    </div>
                    <div className="flex">
                        <div className="w-[7em] flex-shrink-0 mr-2" />
                        {/* 转运填写示例提示（主题紫色） */}
                        <div className="text-purple-600">
                            {String(t('tms.task.drawer.transitTable.hint'))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TransitSection;
